import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, formatApiError } from "@/lib/api";
import { useList } from "@/hooks/useList";

export const ConfirmDelete = ({ item, label, onCancel, onConfirm }) => (
  <AlertDialog open={!!item} onOpenChange={(o) => !o && onCancel()}>
    <AlertDialogContent className="bg-[#12131A] border-[#FF2A55]/50 rounded-none">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display uppercase text-2xl">Delete "{label}"?</AlertDialogTitle>
        <AlertDialogDescription className="text-zinc-400">This removes it from your site. It can't be undone.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="collection-delete-cancel" className="rounded-none">Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid="collection-delete-confirm" onClick={onConfirm} className="rounded-none bg-[#FF2A55] text-black hover:bg-[#ff5577]">Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const AdminCollection = ({ name, path, kicker, title, Form, Preview, label, Toolbar }) => {
  const { items, loading, reload } = useList(path);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const save = async (body) => {
    try {
      if (editing?.id) await api.put(`${path}/${editing.id}`, body);
      else await api.post(path, body);
      toast.success("SAVED");
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`${path}/${deleting.id}`);
      toast.success("DELETED");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setDeleting(null);
  };

  return (
    <section className="px-5 sm:px-10 py-10 max-w-5xl" data-testid={`admin-${name}`}>
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <p className="font-mono text-xs tracking-[0.35em] text-[#F5A524]">// {kicker}</p>
          <h1 className="font-display font-black uppercase text-5xl sm:text-6xl">{title} <span className="text-[#F5A524]">{items.length}</span></h1>
        </div>
        <button data-testid={`admin-${name}-new-btn`} onClick={() => setEditing({})}
          className="clip-tab flex items-center gap-2 bg-[#F5A524] text-black font-mono font-bold text-sm tracking-[0.2em] px-6 py-3 hover:bg-white transition-colors">
          <Plus size={16} /> ADD NEW
        </button>
      </div>
      {Toolbar && <Toolbar reload={reload} />}
      {loading ? <p className="font-mono text-zinc-400">LOADING...</p> : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`admin-${name}-row-${it.id}`} className="flex items-center gap-4 p-3 bg-[#12131A] border border-[#F5A524]/15 hover:border-[#F5A524]/60 transition-colors">
              <Preview item={it} />
              <button data-testid={`admin-${name}-edit-${it.id}`} onClick={() => setEditing(it)} className="p-2 text-zinc-300 hover:text-[#F5A524]" aria-label="Edit"><Pencil size={16} /></button>
              <button data-testid={`admin-${name}-delete-${it.id}`} onClick={() => setDeleting(it)} className="p-2 text-zinc-300 hover:text-[#FF2A55]" aria-label="Delete"><Trash2 size={16} /></button>
            </div>
          ))}
          {items.length === 0 && <p className="font-mono text-zinc-400 border border-dashed border-[#F5A524]/30 p-10">NOTHING HERE YET. THIS SECTION STAYS HIDDEN ON YOUR SITE UNTIL YOU ADD SOMETHING.</p>}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-lenis-prevent className="bg-[#12131A] border-[#F5A524]/40 max-w-2xl max-h-[90vh] overflow-y-auto rounded-none">
          <DialogHeader><DialogTitle className="font-display font-black uppercase text-3xl">{editing?.id ? "Edit" : "Add"} {label}</DialogTitle></DialogHeader>
          {editing && <Form initial={editing} onSave={save} onCancel={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
      <ConfirmDelete item={deleting} label={deleting?.title || label} onCancel={() => setDeleting(null)} onConfirm={remove} />
    </section>
  );
};
